import api from '@/config/axios';

// Client HTTP tipado do módulo Inovação (/api/inovacao).

export type InovacaoStatus =
  | 'rascunho'
  | 'nova'
  | 'em_analise'
  | 'aguardando_info'
  | 'aprovada'
  | 'em_execucao'
  | 'implantada'
  | 'reprovada';

export type InovacaoMidiaTipo = 'imagem' | 'pdf' | 'audio' | 'video';

export interface InovacaoMidia {
  id: number;
  tipo: InovacaoMidiaTipo;
  nome_original: string;
  mime: string;
  bytes: number;
  duracao_seg: number | null;
  /** Presigned URL (só no detalhe); null = objeto ausente/R2 indisponível. */
  url?: string | null;
}

export interface InovacaoIdeiaResumo {
  id: number;
  numero: string | null;
  titulo: string;
  descricao: string;
  status: InovacaoStatus;
  parecer: string | null;
  youtube_url: string | null;
  apoios_count: number;
  cc_codigo: string;
  cc_nome: string;
  protheus_tenant: 'principal' | 'secundaria';
  enviada_em: string | null;
  created_at: string;
  updated_at: string;
  autor: { id: number; nome: string };
  midias?: InovacaoMidia[];
}

export interface InovacaoComentario {
  id: number;
  texto: string;
  created_at: string;
  autor: { id: number; nome: string };
}

export interface InovacaoHistoricoItem {
  id: number;
  status_de: InovacaoStatus | null;
  status_para: InovacaoStatus;
  observacao: string | null;
  created_at: string;
  user: { id: number; nome: string } | null;
}

export interface InovacaoIdeiaDetalhe extends InovacaoIdeiaResumo {
  /** Quem registrou o último parecer, e quando (card "Parecer da diretoria"). */
  parecer_por: { nome: string | null; em: string; status: InovacaoStatus } | null;
  midias: InovacaoMidia[];
  comentarios: InovacaoComentario[];
  historico: InovacaoHistoricoItem[];
  eu_apoiei: boolean;
  papel: 'autor' | 'diretor' | 'admin';
  pode_decidir: boolean;
  pode_apoiar: boolean;
}

export interface InovacaoKpis {
  aguardando_decisao: number;
  em_execucao: number;
  implantadas_ano: number;
  ano: number;
  tempo_medio_decisao_dias: number | null;
  total: number;
}

export interface InovacaoFila {
  items: InovacaoIdeiaResumo[];
  kpis: InovacaoKpis;
  centros: Array<{ codigo: string; nome: string }>;
}

export type InovacaoDecisao =
  | { acao: 'definir_status'; status: 'em_analise' | 'aprovada' | 'em_execucao' | 'implantada'; parecer?: string }
  | { acao: 'reprovar'; parecer: string }
  | { acao: 'pedir_info'; comentario: string };

export interface InovacaoGamificacao {
  enabled: boolean;
  trimestre?: string;
  meus?: {
    pontos_total: number;
    pontos_trimestre: number;
    posicao: number | null;
    enviadas: number;
    implantadas: number;
    apoios_recebidos: number;
    enviadas_trimestre: number;
  };
  ranking?: Array<{ posicao: number; userId: number; nome: string; pontos: number }>;
}

export interface IdeiaInput {
  ccCodigo: string;
  titulo: string;
  descricao: string;
  youtubeUrl?: string | null;
}

/** Uploads: o axios global tem timeout de 30s — insuficiente para 60MB. */
const UPLOAD_OPTS = { timeout: 300000 };

export const inovacaoApi = {
  async souAvaliador(): Promise<{ isAvaliador: boolean; isAdmin: boolean }> {
    return (await api.get('/inovacao/sou-avaliador')).data;
  },

  async listarCentrosCusto(): Promise<Array<{ codigo: string; nome: string }>> {
    return (await api.get('/inovacao/centros-custo')).data.items;
  },

  async criarIdeia(input: IdeiaInput): Promise<InovacaoIdeiaDetalhe> {
    return (await api.post('/inovacao/ideias', input)).data;
  },

  async atualizarIdeia(id: number, input: Partial<IdeiaInput>): Promise<InovacaoIdeiaDetalhe> {
    return (await api.put(`/inovacao/ideias/${id}`, input)).data;
  },

  async enviar(id: number): Promise<InovacaoIdeiaDetalhe> {
    return (await api.post(`/inovacao/ideias/${id}/enviar`)).data;
  },

  async excluir(id: number): Promise<void> {
    await api.delete(`/inovacao/ideias/${id}`);
  },

  async minhas(): Promise<{ items: InovacaoIdeiaResumo[] }> {
    return (await api.get('/inovacao/ideias/minhas')).data;
  },

  async fila(filtros: { cc?: string; status?: InovacaoStatus } = {}): Promise<InovacaoFila> {
    return (await api.get('/inovacao/ideias', { params: filtros })).data;
  },

  async detalhe(id: number): Promise<InovacaoIdeiaDetalhe> {
    return (await api.get(`/inovacao/ideias/${id}`)).data;
  },

  async decidir(id: number, decisao: InovacaoDecisao): Promise<InovacaoIdeiaDetalhe> {
    return (await api.post(`/inovacao/ideias/${id}/decisao`, decisao)).data;
  },

  async apoiar(id: number): Promise<{ apoios_count: number; eu_apoiei: boolean }> {
    return (await api.post(`/inovacao/ideias/${id}/apoio`)).data;
  },

  async retirarApoio(id: number): Promise<{ apoios_count: number; eu_apoiei: boolean }> {
    return (await api.delete(`/inovacao/ideias/${id}/apoio`)).data;
  },

  async comentar(id: number, texto: string): Promise<InovacaoComentario> {
    return (await api.post(`/inovacao/ideias/${id}/comentarios`, { texto })).data;
  },

  async uploadMidia(
    id: number,
    file: File | Blob,
    opts: { filename?: string; duracaoSeg?: number; onProgress?: (pct: number) => void } = {}
  ): Promise<InovacaoMidia> {
    const form = new FormData();
    form.append('file', file, opts.filename ?? (file as File).name ?? 'arquivo');
    if (opts.duracaoSeg != null) form.append('duracaoSeg', String(Math.round(opts.duracaoSeg)));
    const res = await api.post(`/inovacao/ideias/${id}/midias`, form, {
      ...UPLOAD_OPTS,
      onUploadProgress: (e) => {
        if (opts.onProgress && e.total) opts.onProgress(Math.round((e.loaded / e.total) * 100));
      },
    });
    return res.data;
  },

  async uploadVideo(
    id: number,
    file: File,
    opts: { onProgress?: (pct: number) => void } = {}
  ): Promise<InovacaoMidia> {
    const form = new FormData();
    form.append('file', file);
    const res = await api.post(`/inovacao/ideias/${id}/midias/video`, form, {
      ...UPLOAD_OPTS,
      onUploadProgress: (e) => {
        if (opts.onProgress && e.total) opts.onProgress(Math.round((e.loaded / e.total) * 100));
      },
    });
    return res.data;
  },

  async removerMidia(id: number, midiaId: number): Promise<void> {
    await api.delete(`/inovacao/ideias/${id}/midias/${midiaId}`);
  },

  async urlMidia(id: number, midiaId: number): Promise<{ url: string | null }> {
    return (await api.get(`/inovacao/ideias/${id}/midias/${midiaId}/url`)).data;
  },

  async gamificacao(): Promise<InovacaoGamificacao> {
    return (await api.get('/inovacao/gamificacao')).data;
  },

  async getConfig(): Promise<{ gamificacaoEnabled: boolean }> {
    return (await api.get('/inovacao/config')).data;
  },

  async putConfig(gamificacaoEnabled: boolean): Promise<{ gamificacaoEnabled: boolean }> {
    return (await api.put('/inovacao/config', { gamificacaoEnabled })).data;
  },
};

export default inovacaoApi;
